// pages/admin/analytics/AdminWasteAnalytics.jsx
// Platform-wide waste analytics for admins: summary KPIs, charts, paginated
// top-collectors table, top-farmers table, and recent requests feed.
import { useEffect, useMemo, useState } from 'react';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  FaTrash,
  FaCheckCircle,
  FaHourglassHalf,
  FaGavel,
  FaBoxes,
  FaDollarSign,
  FaUsers,
  FaPercent,
  FaStar,
} from 'react-icons/fa';
import analyticsApi from '../../../services/analyticsApi';
import {
  StatCard,
  ChartCard,
  doughnutOptions,
  baseBarOptions,
  lineOptions,
  Pagination,
  WASTE_STATUS_COLORS,
  wasteStatusDoughnutData,
} from '../analytics/analyticsShared';

const TABLE_PAGE_SIZE = 10;
const RECENT_PAGE_SIZE = 10;
const TYPE_COLORS = ['#1e5c33', '#7caf64', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];

const STATUS_BADGE = {
  pending:   'bg-amber-50  text-amber-700  ring-amber-200',
  bidding:   'bg-blue-50   text-blue-700   ring-blue-200',
  accepted:  'bg-violet-50 text-violet-700 ring-violet-200',
  completed: 'bg-green-50  text-green-700  ring-green-200',
  canceled:  'bg-red-50    text-red-700    ring-red-200',
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || 'bg-gray-50 text-gray-600 ring-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

export default function AdminWasteAnalytics() {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [collectorPage, setCollectorPage] = useState(1);
  const [farmerPage, setFarmerPage]   = useState(1);
  const [recentPage, setRecentPage]   = useState(1);
  const [collectorSort, setCollectorSort] = useState('total');
  const [farmerSort, setFarmerSort]   = useState('total');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await analyticsApi.getAdminWaste();
        if (mounted) setData(res.analytics);
      } catch {
        /* interceptor handles toasts */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ── Sorting helpers ────────────────────────────────────────────────────────
  const sortedCollectors = useMemo(() => {
    if (!data?.topCollectors) return [];
    return [...data.topCollectors].sort((a, b) => (b[collectorSort] || 0) - (a[collectorSort] || 0));
  }, [data, collectorSort]);

  const sortedFarmers = useMemo(() => {
    if (!data?.topFarmers) return [];
    return [...data.topFarmers].sort((a, b) => (b[farmerSort] || 0) - (a[farmerSort] || 0));
  }, [data, farmerSort]);

  // ── Pagination slices ─────────────────────────────────────────────────────
  const pagedCollectors = useMemo(() => {
    const s = (collectorPage - 1) * TABLE_PAGE_SIZE;
    return sortedCollectors.slice(s, s + TABLE_PAGE_SIZE);
  }, [sortedCollectors, collectorPage]);

  const pagedFarmers = useMemo(() => {
    const s = (farmerPage - 1) * TABLE_PAGE_SIZE;
    return sortedFarmers.slice(s, s + TABLE_PAGE_SIZE);
  }, [sortedFarmers, farmerPage]);

  const pagedRecent = useMemo(() => {
    if (!data?.recent) return [];
    const s = (recentPage - 1) * RECENT_PAGE_SIZE;
    return data.recent.slice(s, s + RECENT_PAGE_SIZE);
  }, [data, recentPage]);

  if (loading) return <div className="flex justify-center items-center h-64 text-gray-500">Loading analytics…</div>;
  if (!data)   return <div className="text-center text-gray-500 mt-10">No waste analytics data available.</div>;

  const { status, monthly, byType } = data;

  // ── Chart datasets ────────────────────────────────────────────────────────
  const monthlyData = {
    labels: monthly.map((m) => m.label),
    datasets: [
      {
        label: 'Requests',
        data: monthly.map((m) => m.count),
        borderColor: '#1e5c33',
        backgroundColor: 'rgba(30,92,51,0.12)',
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
      {
        label: 'Quantity (kg)',
        data: monthly.map((m) => m.quantity),
        borderColor: '#7caf64',
        backgroundColor: 'rgba(124,175,100,0.08)',
        borderWidth: 2,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
        yAxisID: 'y1',
      },
    ],
  };

  const dualAxisOptions = {
    ...lineOptions,
    scales: {
      x: { grid: { display: false } },
      y:  { position: 'left',  grid: { color: 'rgba(0,0,0,0.04)' }, title: { display: true, text: 'Requests' } },
      y1: { position: 'right', grid: { drawOnChartArea: false },    title: { display: true, text: 'Quantity (kg)' } },
    },
  };

  const typeData = {
    labels: byType.map((t) => t._id || 'Unknown'),
    datasets: [
      {
        label: 'Count',
        data: byType.map((t) => t.count),
        backgroundColor: byType.map((_, i) => TYPE_COLORS[i % TYPE_COLORS.length]),
        borderRadius: 3,
      },
    ],
  };

  return (
    <div className="p-1 sm:p-3 space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-primary">Waste Collection Analytics</h1>
        <p className="text-sm text-gray-500">Platform-wide overview of requests, collectors, farmers &amp; activity</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <StatCard label="Total Requests"    value={data.total}                                icon={<FaTrash />} />
        <StatCard label="Open"              value={data.open}                                 icon={<FaHourglassHalf />} accent="text-amber-600" />
        <StatCard label="Completed"         value={status.completed}                          icon={<FaCheckCircle />}   accent="text-primary" />
        <StatCard label="Total Bids"        value={data.totalBids}                            icon={<FaGavel />}         accent="text-blue-600" />
        <StatCard label="Total Qty (kg)"    value={(data.totalQuantity || 0).toLocaleString()} icon={<FaBoxes />}        accent="text-primary" />
        <StatCard label="Est. Value"        value={`$${(data.estimatedValue || 0).toLocaleString()}`} icon={<FaDollarSign />} accent="text-primary" />
        <StatCard label="Collectors"        value={data.activeCollectors}                     icon={<FaUsers />}         accent="text-violet-600" />
        <StatCard label="Completion"        value={`${data.completionRate}%`}                 icon={<FaPercent />}       accent="text-primary" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Status Breakdown">
          <div style={{ height: 250 }}>
            <Doughnut data={wasteStatusDoughnutData(status)} options={doughnutOptions} />
          </div>
        </ChartCard>
        <ChartCard title="Requests &amp; Quantity Over Time" className="lg:col-span-2">
          <div style={{ height: 250 }}>
            <Line data={monthlyData} options={dualAxisOptions} />
          </div>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      {byType?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ChartCard title="Requests by Waste Type" className="lg:col-span-2">
            <div style={{ height: 220 }}>
              <Bar data={typeData} options={baseBarOptions} />
            </div>
          </ChartCard>
          <div />
        </div>
      )}

      {/* ── Top Collectors table ── */}
      <PaginatedTable
        title="Top Waste Collectors"
        sortValue={collectorSort}
        onSortChange={(v) => { setCollectorSort(v); setCollectorPage(1); }}
        sortOptions={[
          { value: 'total',              label: 'Total assigned' },
          { value: 'completed',          label: 'Completed' },
          { value: 'earnings',           label: 'Earnings' },
          { value: 'quantityProcessed',  label: 'Qty processed' },
          { value: 'completionRate',     label: 'Completion rate' },
        ]}
        headers={['Collector', 'Rating', 'Total', 'Active', 'Completed', 'Qty (kg)', 'Earnings', 'Completion', 'Last Activity']}
        page={collectorPage}
        setPage={setCollectorPage}
        total={sortedCollectors.length}
        pageSize={TABLE_PAGE_SIZE}
        emptyText="No collectors yet."
      >
        {pagedCollectors.map((c) => (
          <tr key={String(c.collectorId)} className="hover:bg-gray-50">
            <td className="px-4 py-2">
              <div className="font-medium text-gray-800">{c.name || 'Unknown'}</div>
              <div className="text-xs text-gray-400">{c.email || ''}</div>
            </td>
            <td className="px-3 py-2">
              <span className="inline-flex items-center gap-1 text-amber-500">
                <FaStar className="text-xs" />
                <span className="text-gray-700">{c.rating != null ? Number(c.rating).toFixed(1) : '—'}</span>
              </span>
            </td>
            <td className="px-3 py-2 font-semibold text-gray-800">{c.total}</td>
            <td className="px-3 py-2" style={{ color: WASTE_STATUS_COLORS.accepted }}>{c.active}</td>
            <td className="px-3 py-2" style={{ color: WASTE_STATUS_COLORS.completed }}>{c.completed}</td>
            <td className="px-3 py-2 text-gray-700">{(c.quantityProcessed || 0).toLocaleString()}</td>
            <td className="px-3 py-2 text-gray-700">${(c.earnings || 0).toLocaleString()}</td>
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${c.completionRate}%` }} />
                </div>
                <span className="text-xs text-gray-600">{c.completionRate}%</span>
              </div>
            </td>
            <td className="px-3 py-2 text-xs text-gray-500">
              {c.lastActivity ? new Date(c.lastActivity).toLocaleDateString('en-GB') : '—'}
            </td>
          </tr>
        ))}
      </PaginatedTable>

      {/* ── Top Farmers table ── */}
      <PaginatedTable
        title="Top Farmers"
        sortValue={farmerSort}
        onSortChange={(v) => { setFarmerSort(v); setFarmerPage(1); }}
        sortOptions={[
          { value: 'total',     label: 'Total requests' },
          { value: 'completed', label: 'Completed' },
          { value: 'quantity',  label: 'Quantity' },
          { value: 'value',     label: 'Est. value' },
        ]}
        headers={['Farmer', 'Total', 'Completed', 'Qty (kg)', 'Est. Value', 'Completion', 'Last Activity']}
        page={farmerPage}
        setPage={setFarmerPage}
        total={sortedFarmers.length}
        pageSize={TABLE_PAGE_SIZE}
        emptyText="No farmers yet."
      >
        {pagedFarmers.map((f) => (
          <tr key={String(f.farmerId)} className="hover:bg-gray-50">
            <td className="px-4 py-2">
              <div className="font-medium text-gray-800">{f.name || 'Unknown'}</div>
              <div className="text-xs text-gray-400">{f.email || ''}</div>
            </td>
            <td className="px-3 py-2 font-semibold text-gray-800">{f.total}</td>
            <td className="px-3 py-2" style={{ color: WASTE_STATUS_COLORS.completed }}>{f.completed}</td>
            <td className="px-3 py-2 text-gray-700">{(f.quantity || 0).toLocaleString()}</td>
            <td className="px-3 py-2 text-gray-700">${(f.value || 0).toLocaleString()}</td>
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${f.completionRate}%` }} />
                </div>
                <span className="text-xs text-gray-600">{f.completionRate}%</span>
              </div>
            </td>
            <td className="px-3 py-2 text-xs text-gray-500">
              {f.lastActivity ? new Date(f.lastActivity).toLocaleDateString('en-GB') : '—'}
            </td>
          </tr>
        ))}
      </PaginatedTable>

      {/* ── Recent Requests table ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recent Requests</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Title / Type</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Farmer</th>
                <th className="text-left px-3 py-2 font-medium">Collector</th>
                <th className="text-left px-3 py-2 font-medium">Qty</th>
                <th className="text-left px-3 py-2 font-medium">Value</th>
                <th className="text-left px-3 py-2 font-medium">Bids</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedRecent.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-gray-400">No requests found.</td>
                </tr>
              )}
              {pagedRecent.map((r) => (
                <tr key={String(r._id)} className="hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{r.title || '—'}</div>
                    <div className="text-xs text-gray-400">{r.type || ''}</div>
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                  <td className="px-3 py-2 text-gray-700">{r.farmerName || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">{r.collectorName?.trim() || <span className="text-gray-400 italic">unassigned</span>}</td>
                  <td className="px-3 py-2 text-gray-700">{r.quantity ? `${r.quantity} ${r.unit || 'kg'}` : '—'}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {r.totalEstimatedValue ? `$${r.totalEstimatedValue.toLocaleString()}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{r.bidCount ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={recentPage}
          setPage={setRecentPage}
          total={data.recent?.length || 0}
          pageSize={RECENT_PAGE_SIZE}
        />
      </div>
    </div>
  );
}

// ── Reusable paginated table shell ────────────────────────────────────────────
function PaginatedTable({
  title, sortValue, onSortChange, sortOptions, headers,
  page, setPage, total, pageSize, emptyText, children,
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
        {sortOptions && (
          <select
            value={sortValue}
            onChange={(e) => onSortChange(e.target.value)}
            className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600"
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>Sort: {o.label}</option>
            ))}
          </select>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              {headers.map((h) => (
                <th key={h} className="text-left px-3 py-2 font-medium first:px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {total === 0 ? (
              <tr>
                <td colSpan={headers.length} className="px-4 py-6 text-center text-gray-400">{emptyText}</td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} setPage={setPage} total={total} pageSize={pageSize} />
    </div>
  );
}
